import type { LeadTypeKey } from './api';

/**
 * Per-type detail field descriptors (spec §11.2–§11.4). Drives the read-only detail panel.
 * The engine renders every captured field generically; this just supplies labels + order.
 */
export interface DetailField {
  key: string;
  /** i18n key (under `leads.fields.*`) for the field label, resolved at render. */
  labelKey: string;
  /** Render hint. `mono` for IDs/locale, `bool` for consent flags, default plain text. */
  kind?: 'text' | 'mono' | 'bool';
}

// `source` is not listed here: it renders in the dedicated Source section (LeadSource.tsx).
const COMMON_TAIL: DetailField[] = [
  { key: 'locale', labelKey: 'leads.fields.locale', kind: 'mono' },
];

const DETAIL_FIELDS: Record<LeadTypeKey, DetailField[]> = {
  contact: [
    { key: 'full_name', labelKey: 'leads.fields.fullName' },
    { key: 'email', labelKey: 'leads.fields.email' },
    { key: 'phone', labelKey: 'leads.fields.phone' },
    { key: 'company', labelKey: 'leads.fields.company' },
    { key: 'inquiry_type', labelKey: 'leads.fields.inquiryType', kind: 'mono' },
    { key: 'subject', labelKey: 'leads.fields.subject' },
    { key: 'country', labelKey: 'leads.fields.country' },
    { key: 'region', labelKey: 'leads.fields.region' },
    { key: 'industry', labelKey: 'leads.fields.industry' },
    { key: 'project_location', labelKey: 'leads.fields.projectLocation' },
    { key: 'budget', labelKey: 'leads.fields.budget' },
    { key: 'product_family', labelKey: 'leads.fields.productFamily' },
    { key: 'application', labelKey: 'leads.fields.application' },
    { key: 'routed_to', labelKey: 'leads.fields.routedTo' },
    { key: 'message', labelKey: 'leads.fields.message' },
    { key: 'pdpl_consent', labelKey: 'leads.fields.pdplConsent', kind: 'bool' },
    ...COMMON_TAIL,
  ],
  quotes: [
    { key: 'full_name', labelKey: 'leads.fields.fullName' },
    { key: 'email', labelKey: 'leads.fields.email' },
    { key: 'phone', labelKey: 'leads.fields.phone' },
    { key: 'company', labelKey: 'leads.fields.company' },
    { key: 'country', labelKey: 'leads.fields.country' },
    { key: 'product_id', labelKey: 'leads.fields.productId', kind: 'mono' },
    { key: 'family_id', labelKey: 'leads.fields.familyId', kind: 'mono' },
    { key: 'quantity', labelKey: 'leads.fields.quantity' },
    { key: 'requirements', labelKey: 'leads.fields.requirements' },
    { key: 'routed_to', labelKey: 'leads.fields.routedTo' },
    { key: 'message', labelKey: 'leads.fields.message' },
    { key: 'consent', labelKey: 'leads.fields.consent', kind: 'bool' },
    ...COMMON_TAIL,
  ],
  applications: [
    { key: 'full_name', labelKey: 'leads.fields.fullName' },
    { key: 'email', labelKey: 'leads.fields.email' },
    { key: 'phone', labelKey: 'leads.fields.phone' },
    { key: 'job_posting_id', labelKey: 'leads.fields.jobPostingId', kind: 'mono' },
    { key: 'portfolio_url', labelKey: 'leads.fields.portfolio' },
    { key: 'linkedin', labelKey: 'leads.fields.linkedin' },
    { key: 'cover_letter', labelKey: 'leads.fields.coverLetter' },
    { key: 'consent', labelKey: 'leads.fields.consent', kind: 'bool' },
    ...COMMON_TAIL,
  ],
  newsletter: [
    { key: 'email', labelKey: 'leads.fields.email' },
    { key: 'status', labelKey: 'leads.fields.status', kind: 'mono' },
    { key: 'confirmed_at', labelKey: 'leads.fields.confirmedAt', kind: 'mono' },
    { key: 'unsubscribed_at', labelKey: 'leads.fields.unsubscribedAt', kind: 'mono' },
    ...COMMON_TAIL,
  ],
};

export function getDetailFields(type: LeadTypeKey): DetailField[] {
  return DETAIL_FIELDS[type] ?? [];
}

/** Newsletter status values for its dedicated status filter. */
export const NEWSLETTER_STATUSES = ['pending_confirmation', 'confirmed', 'unsubscribed'] as const;
